import type { Repair } from '../types/typesRepair';
import { parseFlexibleDateToIso } from './date';

export type RepairPaymentDisplayState = {
  isCompleted: boolean;
  isFailed: boolean;
  isCancelled: boolean;
  /** החזר מלא בלבד – לא החזר חלקי (בחלקי נשאר «שולם» עם יתרה) */
  isRefunded: boolean;
};

export type RepairPaymentMethodKind = 'credit' | 'cash' | 'invoice' | 'bank_transfer' | 'unknown';

export type RepairPaymentAmountVisualState = {
  color: string;
  fontWeight: number | string;
  paymentMethodKind: RepairPaymentMethodKind;
  /** התשלום אושר לפי כללי אמצעי התשלום */
  isPaymentApproved: boolean;
  canMarkInvoice: boolean;
  /** מצב חריג (כשל / ביטול / החזר) — דורס אייקון אמצעי תשלום */
  exceptionalState?: 'failed' | 'cancelled' | 'refunded';
};

export function repairPriorityInvoiceIssued(repair: Repair | Record<string, unknown>): boolean {
  const raw = (repair as { priorityInvoiceIssued?: boolean | string | number }).priorityInvoiceIssued;
  return raw === true || raw === 1 || String(raw || '').trim().toLowerCase() === 'true';
}

export function repairCreditInvoiceIssued(repair: Repair | Record<string, unknown>): boolean {
  const raw = (repair as { creditInvoiceIssued?: boolean | string | number }).creditInvoiceIssued;
  return raw === true || raw === 1 || String(raw || '').trim().toLowerCase() === 'true';
}

/** בוצע החזר כספי (מלא או חלקי) — דורש חשבונית זיכוי */
export function repairHasRefund(repair: Repair | Record<string, unknown>): boolean {
  const history = (repair as { paymentHistory?: Array<{ type?: string }> }).paymentHistory;
  if (Array.isArray(history)) {
    const hasRefundEntry = history.some((h) => {
      const t = String(h?.type || '').trim();
      return t === 'refund_full' || t === 'refund_partial';
    });
    if (hasRefundEntry) return true;
  }
  const refunded = Number((repair as { paymentRefundedAmount?: number }).paymentRefundedAmount || 0);
  if (refunded > 0.009) return true;
  return resolveRepairPaymentDisplayState(repair).isRefunded;
}

const LOCAL_PRIORITY_INVOICE_STORAGE_KEY = 'repairLab.priorityInvoiceIssued.v1';

type LocalPriorityInvoiceMarks = Record<string, string>;

function readLocalPriorityInvoiceMarksMap(): LocalPriorityInvoiceMarks {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_PRIORITY_INVOICE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const next: LocalPriorityInvoiceMarks = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([repairId, issuedAt]) => {
      const id = String(repairId || '').trim();
      const at = String(issuedAt || '').trim();
      if (id && at) next[id] = at;
    });
    return next;
  } catch {
    return {};
  }
}

function writeLocalPriorityInvoiceMarksMap(marks: LocalPriorityInvoiceMarks): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCAL_PRIORITY_INVOICE_STORAGE_KEY, JSON.stringify(marks));
  } catch {
    /* quota / private mode */
  }
}

export function setLocalPriorityInvoiceMark(repairId: string, issuedAt: string): void {
  const id = String(repairId || '').trim();
  const at = String(issuedAt || '').trim();
  if (!id || !at) return;
  const marks = readLocalPriorityInvoiceMarksMap();
  marks[id] = at;
  writeLocalPriorityInvoiceMarksMap(marks);
}

export function applyLocalPriorityInvoiceMarks<T extends Repair>(repair: T): T {
  const id = String(repair.id || '').trim();
  if (!id) return repair;

  if (repairPriorityInvoiceIssued(repair)) {
    const at = String(repair.priorityInvoiceIssuedAt || '').trim();
    if (at) setLocalPriorityInvoiceMark(id, at);
    return repair;
  }

  const localAt = readLocalPriorityInvoiceMarksMap()[id];
  if (!localAt) return repair;
  return {
    ...repair,
    priorityInvoiceIssued: true,
    priorityInvoiceIssuedAt: localAt,
  };
}

/** מיזוג שורה ברענון רשימה — לא לאבד סימון חשבונית אם התשובה מהשרת עדיין לא מעודכנת */
export function mergeRepairListRowFromRefresh(
  prev: Repair | undefined,
  incoming: Repair,
): Repair {
  if (!prev) return incoming;
  const merged: Repair = { ...prev, ...incoming };
  if (repairPriorityInvoiceIssued(prev) && !repairPriorityInvoiceIssued(incoming)) {
    merged.priorityInvoiceIssued = true;
    merged.priorityInvoiceIssuedAt =
      String(prev.priorityInvoiceIssuedAt || incoming.priorityInvoiceIssuedAt || '').trim() || undefined;
  }
  return merged;
}

/** יש סכום גבייה בקריאה (מחיר או נטו אחרי החזר) */
function parsePaymentFilterDate(value?: string | null): Date | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) return direct;
  const flexibleIso = parseFlexibleDateToIso(raw);
  if (!flexibleIso) return null;
  const parsed = new Date(flexibleIso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function pickFirstPaymentFilterDate(
  ...candidates: Array<string | undefined | null>
): Date | null {
  for (const candidate of candidates) {
    const parsed = parsePaymentFilterDate(String(candidate || ''));
    if (parsed) return parsed;
  }
  return null;
}

export function repairHasChargeableAmount(repair: Repair | Record<string, unknown>): boolean {
  const displayAmount = getRepairNetPaymentAmount(repair);
  const quotedAmount = Number((repair as { paymentAmount?: number }).paymentAmount || 0);
  return displayAmount > 0.009 || quotedAmount > 0.009;
}

/** סוג אמצעי התשלום לפי שדה הקריאה (עם הסקה מגבייה באשראי לקריאות ישנות) */
export function resolveRepairPaymentMethodKind(
  repair: Repair | Record<string, unknown>,
): RepairPaymentMethodKind {
  const paymentMethod = String((repair as { paymentMethod?: string }).paymentMethod || '').trim().toLowerCase();
  if (
    paymentMethod.includes('מזומן') ||
    paymentMethod === 'cash' ||
    paymentMethod === 'מזומן בלבד'
  ) {
    return 'cash';
  }
  if (paymentMethod.includes('חשבונית') || paymentMethod === 'invoice') {
    return 'invoice';
  }
  if (
    paymentMethod.includes('העברה') ||
    paymentMethod.includes('בנקאית') ||
    paymentMethod.includes('transfer') ||
    paymentMethod.includes('bank')
  ) {
    return 'bank_transfer';
  }
  if (
    paymentMethod.includes('אשראי') ||
    paymentMethod.includes('credit') ||
    paymentMethod.includes('card')
  ) {
    return 'credit';
  }

  const gatewayPaid = Number((repair as { paymentGatewayPaidAmount?: number }).paymentGatewayPaidAmount || 0);
  const gatewayOrderId = String((repair as { paymentGatewayOrderId?: string }).paymentGatewayOrderId || '').trim();
  if (gatewayPaid > 0.009 || gatewayOrderId) return 'credit';

  return 'unknown';
}

/** גבייה שדורשת סימון ידני של חשבונית פריוריטי */
export function repairTracksPriorityInvoice(repair: Repair | Record<string, unknown>): boolean {
  if (!repairHasChargeableAmount(repair)) return false;
  return resolveRepairPaymentMethodKind(repair) === 'invoice';
}

function repairHasPaymentEvidence(repair: Repair | Record<string, unknown>): boolean {
  return Boolean(
    String((repair as { paymentDate?: string }).paymentDate || '').trim() ||
      String((repair as { paymentCompletedAt?: string }).paymentCompletedAt || '').trim(),
  );
}

/** האם התשלום אושר לפי אמצעי התשלום */
export function isRepairPaymentApproved(
  repair: Repair | Record<string, unknown>,
  kind: RepairPaymentMethodKind = resolveRepairPaymentMethodKind(repair),
): boolean {
  const display = resolveRepairPaymentDisplayState(repair);
  switch (kind) {
    case 'credit':
      return display.isCompleted && !display.isRefunded;
    case 'invoice':
      return repairPriorityInvoiceIssued(repair);
    case 'cash':
      return repairHasChargeableAmount(repair);
    case 'bank_transfer':
      return (
        repairPriorityInvoiceIssued(repair) ||
        repairHasPaymentEvidence(repair) ||
        (display.isCompleted && !display.isRefunded)
      );
    default:
      return display.isCompleted || repairPriorityInvoiceIssued(repair);
  }
}

/**
 * תאריך גבייה לסינון לפי טווח תאריכים בלבד (בלי סינון אמצעי תשלום) —
 * כל קריאה עם סכום גבייה ותאריך גבייה/סליקה/עדכון סטטוס ידוע.
 */
export function getRepairBroadPaymentDateForFilter(
  repair: Repair | Record<string, unknown>,
): Date | null {
  if (!repairHasChargeableAmount(repair)) return null;
  return pickFirstPaymentFilterDate(
    (repair as { paymentDate?: string }).paymentDate,
    (repair as { paymentCompletedAt?: string }).paymentCompletedAt,
    (repair as { paymentStatusUpdatedAt?: string }).paymentStatusUpdatedAt,
    (repair as { priorityInvoiceIssuedAt?: string }).priorityInvoiceIssuedAt,
  );
}

/**
 * תאריך תשלום לסינון — רק כשיש גבייה/אישור לפי אמצעי התשלום.
 * לא משתמש ב-updatedAt/createdAt כדי שלא ייכנסו קריאות שלא שולמו באותו יום.
 */
export function getRepairPaymentDateForFilter(
  repair: Repair | Record<string, unknown>,
): Date | null {
  if (!repairHasChargeableAmount(repair)) return null;

  const kind = resolveRepairPaymentMethodKind(repair);

  switch (kind) {
    case 'credit': {
      if (!isRepairPaymentApproved(repair, 'credit')) return null;
      return pickFirstPaymentFilterDate(
        (repair as { paymentCompletedAt?: string }).paymentCompletedAt,
        (repair as { paymentDate?: string }).paymentDate,
      );
    }
    case 'cash': {
      if (!isRepairPaymentApproved(repair, 'cash')) return null;
      return pickFirstPaymentFilterDate(
        (repair as { paymentStatusUpdatedAt?: string }).paymentStatusUpdatedAt,
        (repair as { paymentDate?: string }).paymentDate,
      );
    }
    case 'invoice': {
      if (!isRepairPaymentApproved(repair, 'invoice')) return null;
      return pickFirstPaymentFilterDate(
        (repair as { priorityInvoiceIssuedAt?: string }).priorityInvoiceIssuedAt,
        (repair as { paymentDate?: string }).paymentDate,
      );
    }
    case 'bank_transfer': {
      if (!isRepairPaymentApproved(repair, 'bank_transfer')) return null;
      return pickFirstPaymentFilterDate(
        (repair as { paymentCompletedAt?: string }).paymentCompletedAt,
        (repair as { paymentDate?: string }).paymentDate,
        (repair as { priorityInvoiceIssuedAt?: string }).priorityInvoiceIssuedAt,
        (repair as { paymentStatusUpdatedAt?: string }).paymentStatusUpdatedAt,
      );
    }
    default: {
      if (!isRepairPaymentApproved(repair)) return null;
      return pickFirstPaymentFilterDate(
        (repair as { paymentCompletedAt?: string }).paymentCompletedAt,
        (repair as { paymentDate?: string }).paymentDate,
        (repair as { priorityInvoiceIssuedAt?: string }).priorityInvoiceIssuedAt,
        (repair as { paymentStatusUpdatedAt?: string }).paymentStatusUpdatedAt,
      );
    }
  }
}

type HistoryEntry = { type?: string; at?: string; amount?: number };

/** סכום שנסלק במקור (לא משתנה אחרי החזר חלקי) */
export function getRepairOriginalChargedAmount(repair: Repair | Record<string, unknown>): number {
  const gateway = Number((repair as { paymentGatewayPaidAmount?: number }).paymentGatewayPaidAmount || 0);
  if (gateway > 0.009) return gateway;
  const refunded = Number((repair as { paymentRefundedAmount?: number }).paymentRefundedAmount || 0);
  const paymentAmount = Number((repair as { paymentAmount?: number }).paymentAmount || 0);
  // אם שדה המחיר כבר עודכן לנטו אחרי החזר – משחזרים את הסכום המקורי
  if (refunded > 0.009 && paymentAmount > 0.009) {
    return paymentAmount + refunded;
  }
  return paymentAmount;
}

/** האם ביטול לכידה הוא אחרי גבייה אמיתית (לא סימון שגוי) */
export function repairCaptureCancelWasAfterRealCollection(repair: Repair | Record<string, unknown>): boolean {
  const latest = getLatestPaymentHistoryEntry(repair);
  if (latest?.type === 'payment_mark_reverted') return false;

  const orderId = String(
    (repair as { paymentGatewayOrderId?: string }).paymentGatewayOrderId ||
      (repair as { paymentGatewayOrderKey?: string }).paymentGatewayOrderKey ||
      '',
  ).trim();
  const paymentDate = String((repair as { paymentDate?: string }).paymentDate || '').trim();
  const gateway = Number((repair as { paymentGatewayPaidAmount?: number }).paymentGatewayPaidAmount || 0);

  if (latest?.type === 'capture_cancelled' || Boolean((repair as { paymentCaptureCancelled?: boolean }).paymentCaptureCancelled)) {
    if (!orderId && !paymentDate) return false;
  }

  if (gateway > 0.009 && orderId) return true;

  const history = (repair as { paymentHistory?: HistoryEntry[] }).paymentHistory;
  if (!Array.isArray(history)) return false;
  const cancelAt = String((repair as { paymentCaptureCancelledAt?: string }).paymentCaptureCancelledAt || latest?.at || '').trim();
  const cancelMs = cancelAt ? new Date(cancelAt).getTime() : NaN;
  return history.some((h) => {
    if (!h || h.type !== 'completed') return false;
    const amt = Number(h.amount || 0);
    if (amt <= 0.009) return false;
    if (!Number.isNaN(cancelMs) && String(h.at || '').trim()) {
      const entryMs = new Date(String(h.at)).getTime();
      if (!Number.isNaN(entryMs) && entryMs > cancelMs) return false;
    }
    return Boolean(orderId || paymentDate);
  });
}

/** סה"כ שנגבה (סליקה ראשונה + תוספות, לפי לוג) */
export function getRepairTotalCollectedAmount(repair: Repair | Record<string, unknown>): number {
  const latest = getLatestPaymentHistoryEntry(repair);
  if (latest?.type === 'payment_mark_reverted') return 0;

  const explicit = Number((repair as { paymentTotalCollected?: number }).paymentTotalCollected || 0);
  const history = (repair as { paymentHistory?: HistoryEntry[] }).paymentHistory;

  if (latest?.type === 'capture_cancelled') {
    const cancelAt = String(latest.at || '').trim();
    const cancelMs = cancelAt ? new Date(cancelAt).getTime() : NaN;
    if (Array.isArray(history) && !Number.isNaN(cancelMs)) {
      let afterCancel = 0;
      for (const h of history) {
        const type = String(h?.type || '').trim();
        const amt = Number(h?.amount || 0);
        if (type !== 'supplement_completed' || !Number.isFinite(amt) || amt <= 0) continue;
        const entryMs = new Date(String(h?.at || '')).getTime();
        if (!Number.isNaN(entryMs) && entryMs > cancelMs) afterCancel += amt;
      }
      if (afterCancel > 0.009) return Math.round(afterCancel * 100) / 100;
    }
    if (explicit > 0.009) return explicit;
    return 0;
  }

  if (explicit > 0.009) return explicit;
  const fromHistory = computePaymentTotalCollectedFromHistory(history);
  if (fromHistory > 0.009) return fromHistory;
  return getRepairOriginalChargedAmount(repair);
}

function computePaymentTotalCollectedFromHistory(history?: HistoryEntry[] | unknown): number {
  if (!Array.isArray(history)) return 0;
  let total = 0;
  for (const h of history) {
    const type = String(h?.type || '').trim();
    const amt = Number(h?.amount || 0);
    if (!Number.isFinite(amt) || amt <= 0) continue;
    if (type === 'completed' || type === 'supplement_completed') total += amt;
    // החזרים לא מפחיתים כאן — getRepairNetPaymentAmount מפחית paymentRefundedAmount בנפרד
  }
  return Math.max(0, Math.round(total * 100) / 100);
}

/** יתרה בפועל אחרי החזרים: מקורי פחות מה שכבר הוחזר */
export function getRepairNetPaymentAmount(repair: Repair | Record<string, unknown>): number {
  const totalCollected = getRepairTotalCollectedAmount(repair);
  const refunded = Number((repair as { paymentRefundedAmount?: number }).paymentRefundedAmount || 0);
  const paymentAmount = Number((repair as { paymentAmount?: number }).paymentAmount || 0);
  if (refunded > 0.009 && paymentAmount > 0.009) {
    const reconstructedGross = Math.round((paymentAmount + refunded) * 100) / 100;
    // paymentAmount מתעדכן ליתרה אחרי החזר חלקי — אם totalCollected נשמר בטעות כנטו, לא מפחיתים שוב
    if (totalCollected + 0.02 < reconstructedGross) {
      return Math.max(0, Math.round(paymentAmount * 100) / 100);
    }
  }
  return Math.max(0, Math.round((totalCollected - refunded) * 100) / 100);
}

/** האירוע האחרון בלוג הגבייה (לפי `at`), אם קיים */
export function getLatestPaymentHistoryEntry(
  repair: Repair | Record<string, unknown>,
): HistoryEntry | null {
  const raw = (repair as { paymentHistory?: unknown }).paymentHistory;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const valid = raw.filter(
    (e: unknown): e is HistoryEntry =>
      Boolean(e) && typeof e === 'object' && typeof (e as HistoryEntry).type === 'string' && String((e as HistoryEntry).at || '').trim() !== '',
  );
  if (valid.length === 0) return null;
  valid.sort((a, b) => {
    const ta = new Date(String(a.at)).getTime();
    const tb = new Date(String(b.at)).getTime();
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return tb - ta;
  });
  return valid[0] ?? null;
}

/**
 * תצוגת איקון/צבע מחיר בטבלה: לפי האירוע האחרון ב-paymentHistory אם יש;
 * אחרת לפי שדות — לא מציגים «ביטול לכידה» אם כבר יש סליקה מוצלחת אחרי (גם אם paymentCaptureCancelled נשאר true).
 */
export function resolveRepairPaymentDisplayState(repair: Repair | Record<string, unknown>): RepairPaymentDisplayState {
  const status = String((repair as { paymentStatus?: string }).paymentStatus || '').trim();
  const refundedAmount = Number((repair as { paymentRefundedAmount?: number }).paymentRefundedAmount || 0);
  const hasActualRefund = refundedAmount > 0.009;
  const normalizedStatus = status === 'refunded' && !hasActualRefund ? 'completed' : status;
  const hasPaymentEvidence = Boolean(
    String((repair as { paymentDate?: string }).paymentDate || '').trim() ||
      String((repair as { paymentCompletedAt?: string }).paymentCompletedAt || '').trim(),
  );
  const chargedAmount = Number(
    (repair as { paymentGatewayPaidAmount?: number }).paymentGatewayPaidAmount ||
      (repair as { paymentAmount?: number }).paymentAmount ||
      0,
  );

  const latest = getLatestPaymentHistoryEntry(repair);
  if (latest?.type === 'completed' || latest?.type === 'supplement_completed') {
    return { isCompleted: true, isFailed: false, isCancelled: false, isRefunded: false };
  }
  if (latest?.type === 'payment_mark_reverted') {
    return { isCompleted: false, isFailed: false, isCancelled: false, isRefunded: false };
  }
  if (latest?.type === 'capture_cancelled') {
    if (!repairCaptureCancelWasAfterRealCollection(repair)) {
      return { isCompleted: false, isFailed: false, isCancelled: false, isRefunded: false };
    }
    return { isCompleted: false, isFailed: false, isCancelled: true, isRefunded: false };
  }
  if (latest?.type === 'refund_full') {
    return { isCompleted: false, isFailed: false, isCancelled: false, isRefunded: true };
  }
  if (latest?.type === 'refund_partial') {
    return { isCompleted: true, isFailed: false, isCancelled: false, isRefunded: false };
  }

  // callback סימן failed אבל יש ראיות גבייה (סכום + תאריך) – כמו בטבלה/דשבורד
  if (
    normalizedStatus === 'failed' &&
    hasPaymentEvidence &&
    Number.isFinite(chargedAmount) &&
    chargedAmount > 0.009
  ) {
    return { isCompleted: true, isFailed: false, isCancelled: false, isRefunded: false };
  }

  const isCompleted =
    normalizedStatus === 'completed' ||
    (!normalizedStatus && hasPaymentEvidence && Number.isFinite(chargedAmount) && chargedAmount > 0.009);
  const isFailed = normalizedStatus === 'failed';
  const isCancelled =
    (normalizedStatus === 'capture_cancelled' ||
      (!isCompleted && Boolean((repair as { paymentCaptureCancelled?: boolean }).paymentCaptureCancelled))) &&
    repairCaptureCancelWasAfterRealCollection(repair);
  const refundedAmountForStatus = Number((repair as { paymentRefundedAmount?: number }).paymentRefundedAmount || 0);
  const chargedForStatus = getRepairOriginalChargedAmount(repair);
  const isFullRefund =
    normalizedStatus === 'refunded' ||
    (refundedAmountForStatus > 0.009 && refundedAmountForStatus >= chargedForStatus - 0.009);
  const isPartialRefund =
    refundedAmountForStatus > 0.009 && !isFullRefund && chargedForStatus > 0.009;
  const isRefunded = isFullRefund;
  const isCompletedWithPartial =
    isPartialRefund && (isCompleted || hasPaymentEvidence);
  return {
    isCompleted: isCompleted || isCompletedWithPartial,
    isFailed,
    isCancelled,
    isRefunded,
  };
}

/** החזר מלא / חלקי / ביטול לכידה / זיכוי — לסינון רשימת קריאות */
export function repairHasPaymentReversal(repair: Repair | Record<string, unknown>): boolean {
  const history = (repair as { paymentHistory?: HistoryEntry[] }).paymentHistory;
  if (Array.isArray(history)) {
    const hasRefundEntry = history.some((h) => {
      const t = String(h?.type || '').trim();
      return t === 'refund_full' || t === 'refund_partial';
    });
    if (hasRefundEntry) return true;
  }

  const refunded = Number((repair as { paymentRefundedAmount?: number }).paymentRefundedAmount || 0);
  if (refunded > 0.009) return true;

  const status = String((repair as { paymentStatus?: string }).paymentStatus || '').trim().toLowerCase();
  if (status === 'refunded') return true;

  const latest = getLatestPaymentHistoryEntry(repair);
  if (latest?.type === 'payment_mark_reverted') return false;

  if (status === 'capture_cancelled' && repairCaptureCancelWasAfterRealCollection(repair)) return true;
  if (
    Boolean((repair as { paymentCaptureCancelled?: boolean }).paymentCaptureCancelled) &&
    repairCaptureCancelWasAfterRealCollection(repair)
  ) {
    return true;
  }

  if (Array.isArray(history)) {
    return history.some((h) => {
      const t = String(h?.type || '').trim();
      if (t === 'capture_cancelled') return repairCaptureCancelWasAfterRealCollection(repair);
      return false;
    });
  }
  return false;
}

/** לסינון «גויב בלבד» (אשראי): האם יש סליקה תקפה כרגע — לא נחסמים בגלל ביטול לכידה ישן אחרי סליקה מחדש */
export function repairHasEffectiveCreditCollection(repair: Repair | Record<string, unknown>): boolean {
  const { isCompleted } = resolveRepairPaymentDisplayState(repair);
  if (!isCompleted) return false;
  const paymentMethod = String((repair as { paymentMethod?: string }).paymentMethod || '').trim().toLowerCase();
  const gatewayPaid = Number((repair as { paymentGatewayPaidAmount?: number }).paymentGatewayPaidAmount || 0) || 0;
  const paymentAmount = Number((repair as { paymentAmount?: number }).paymentAmount || 0) || 0;
  const isCreditLike =
    paymentMethod.includes('אשראי') ||
    paymentMethod.includes('credit') ||
    paymentMethod.includes('card') ||
    gatewayPaid > 0 ||
    String((repair as { paymentGatewayOrderId?: string }).paymentGatewayOrderId || '').trim() !== '';
  if (!isCreditLike) return false;
  return Math.max(0, gatewayPaid || paymentAmount) > 0.009;
}

/** צבע ואייקון לעמודת מחיר בטבלה */
export function resolveRepairPaymentAmountVisualState(
  repair: Repair | Record<string, unknown>,
  options: { canMarkInvoice?: boolean } = {},
): RepairPaymentAmountVisualState | null {
  const { isFailed, isCancelled, isRefunded } = resolveRepairPaymentDisplayState(repair);
  const hasAnyAmount = repairHasChargeableAmount(repair);
  if (!hasAnyAmount && !isRefunded) {
    return null;
  }

  if (isFailed) {
    return {
      color: '#d32f2f',
      fontWeight: 'normal',
      paymentMethodKind: resolveRepairPaymentMethodKind(repair),
      isPaymentApproved: false,
      canMarkInvoice: false,
      exceptionalState: 'failed',
    };
  }
  if (isCancelled) {
    return {
      color: '#ed6c02',
      fontWeight: 'normal',
      paymentMethodKind: resolveRepairPaymentMethodKind(repair),
      isPaymentApproved: false,
      canMarkInvoice: false,
      exceptionalState: 'cancelled',
    };
  }
  if (isRefunded) {
    return {
      color: '#1976d2',
      fontWeight: 'normal',
      paymentMethodKind: resolveRepairPaymentMethodKind(repair),
      isPaymentApproved: false,
      canMarkInvoice: false,
      exceptionalState: 'refunded',
    };
  }

  const paymentMethodKind = resolveRepairPaymentMethodKind(repair);
  const isPaymentApproved = isRepairPaymentApproved(repair, paymentMethodKind);
  const canMarkInvoice =
    paymentMethodKind === 'invoice' &&
    !isPaymentApproved &&
    Boolean(options.canMarkInvoice);

  return {
    color: isPaymentApproved ? '#2e7d32' : '#5d4037',
    fontWeight: 'bold',
    paymentMethodKind,
    isPaymentApproved,
    canMarkInvoice,
  };
}
